import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  doc,
  getDoc,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

/* ========================= INDUSTRY OPTIONS ========================= */
const INDUSTRIES = [
  "HealthTech","FinTech","EdTech","AgriTech","ClimateTech","BioTech",
  "DeepTech","AI / ML","SaaS","E-commerce","Consumer","Gaming",
  "Web3 / Blockchain","Cybersecurity","IoT","Robotics","Mobility",
  "SpaceTech","HRTech","Other",
];

function SharedWithMe() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [shared, setShared] = useState([]);
  const [evaluationMap, setEvaluationMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [openSection, setOpenSection] = useState("new");

  /* ========================= REALTIME SHARES ========================= */
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);

    const sharesQuery = query(
      collection(db, "shares"),
      where("evaluatorId", "==", user.uid)
    );

    const unsubShares = onSnapshot(sharesQuery, async (snap) => {
      if (snap.empty) {
        setShared([]);
        setLoading(false);
        return;
      }

      const founderIds = snap.docs.map((d) => d.data().founderId).filter(Boolean);

      const founderSnaps = await Promise.all(
        founderIds.map((id) => getDoc(doc(db, "users", id)))
      );

      const founderMap = {};
      founderSnaps.forEach((s) => { if (s.exists()) founderMap[s.id] = s.data(); });

      const rows = snap.docs.map((s) => {
        const data = s.data();
        const founder = founderMap[data.founderId];
        if (!founder) return null;

        return {
          shareId: s.id,
          founderId: data.founderId,
          startupName: founder.startupName || "Untitled Startup",
          founderName: founder.fullName || "Unknown Founder",
          industries: Array.isArray(founder.industries)
            ? founder.industries
            : founder.industry ? [founder.industry] : [],
        };
      }).filter(Boolean);

      setShared(rows);
      setLoading(false);
    });

    return () => unsubShares();
  }, [user?.uid]);

  /* ========================= REALTIME EVALUATIONS ========================= */
  useEffect(() => {
    if (!user?.uid) return;

    const evalQuery = query(
      collection(db, "evaluations"),
      where("evaluatorId", "==", user.uid)
    );

    const unsub = onSnapshot(evalQuery, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.founderId) map[data.founderId] = data.status || "draft";
      });
      setEvaluationMap(map);
    });

    return () => unsub();
  }, [user?.uid]);

  /* ========================= FILTER LOGIC ========================= */
  const applyFilters = (items) => {
    let data = [...items];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (s) =>
          s.startupName.toLowerCase().includes(q) ||
          s.founderName.toLowerCase().includes(q)
      );
    }
    if (industry) data = data.filter((s) => s.industries.includes(industry));
    return data;
  };

  const newItems = applyFilters(shared.filter((s) => !evaluationMap[s.founderId]));
  const inProgressItems = applyFilters(shared.filter((s) => evaluationMap[s.founderId] === "draft"));
  const reviewedItems = applyFilters(shared.filter((s) => evaluationMap[s.founderId] === "submitted"));

  /* ========================= OPEN STARTUP ========================= */
  const handleOpenStartup = async (s) => {
    const status = evaluationMap[s.founderId];
    if (!status) {
      try {
        await addDoc(collection(db, "evaluations"), {
          evaluatorId: user.uid,
          founderId: s.founderId,
          rating: 0,
          notes: "",
          status: "draft",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setEvaluationMap((prev) => ({ ...prev, [s.founderId]: "draft" }));
      } catch { return; }
    }
    navigate(`/evaluator/startup/${s.founderId}?mode=review`);
  };

  /* ========================= CARD ========================= */
  const renderCard = (s, badgeText, badgeColors) => (
    <div
      key={s.shareId}
      className="card"
      onClick={() => handleOpenStartup(s)}
      title="Click to open this startup and begin or continue your evaluation."
      style={{
        padding: 18,
        marginBottom: 12,
        borderRadius: 14,
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3
          style={{ marginBottom: 6 }}
          title="Startup name as provided by the founder."
        >
          {s.startupName}
        </h3>
        <span
          title={
            badgeText === "New"
              ? "You have not started evaluating this startup yet."
              : badgeText === "In Progress"
              ? "You have started an evaluation but haven’t submitted it yet."
              : "This evaluation has been submitted and is now read-only."
          }
          style={{
            fontSize: 12,
            padding: "5px 12px",
            borderRadius: 999,
            fontWeight: 600,
            background: badgeColors.bg,
            color: badgeColors.text,
            whiteSpace: "nowrap"
          }}
        >
          {badgeText}
        </span>
      </div>

      <p
        style={{ fontSize: 14, color: "#64748b" }}
        title="Founder who submitted this startup for evaluation."
      >
        Founder: {s.founderName}
      </p>

      {s.industries.length > 0 && (
        <p
          style={{ fontSize: 12, color: "#475569" }}
          title="Industry tags selected by the founder."
        >
          {s.industries.join(", ")}
        </p>
      )}
    </div>
  );

  /* ========================= UI ========================= */
  return (
    <div>
      <h1
        style={{ marginBottom: 4 }}
        title="Startups that founders have explicitly shared with you for evaluation."
      >
        Shared with me
      </h1>
      <p
        style={{ color: "#64748b", marginBottom: 20 }}
        title="These startups are awaiting your independent review."
      >
        Startups shared with you for evaluation
      </p>

      {/* FILTER BAR */}
      <div
        title="Use these filters to quickly find relevant startups."
        style={{
          display: "flex",
          gap: 14,
          marginBottom: 28,
          padding: 16,
          borderRadius: 16,
          background: "linear-gradient(135deg,#ffffff,#f1f5f9)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
        }}
      >
        <input
          type="text"
          placeholder="🔍 Search startup or founder..."
          value={search}
          title="Search by startup name or founder name."
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
          }}
        />
        <select
          value={industry}
          title="Filter startups by industry."
          onChange={(e) => setIndustry(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
          }}
        >
          <option value="">All industries</option>
          {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
        </select>
      </div>

      {loading ? (
        <p style={{ color: "#64748b" }}>Loading shared startups…</p>
      ) : (
        <>
          {[
            {
              key: "new",
              title: "🔔 New",
              items: newItems,
              badge: "New",
              colors: { bg: "#e0f2fe", text: "#0369a1" }
            },
            {
              key: "progress",
              title: "✏️ In Progress",
              items: inProgressItems,
              badge: "In Progress",
              colors: { bg: "#fef3c7", text: "#92400e" }
            },
            {
              key: "reviewed",
              title: "✅ Reviewed",
              items: reviewedItems,
              badge: "Reviewed",
              colors: { bg: "#dcfce7", text: "#166534" }
            },
          ].map(section =>
            section.items.length > 0 && (
              <div
                key={section.key}
                className="card"
                title="Click to expand or collapse this section."
                style={{ marginBottom: 22, borderRadius: 16, overflow: "hidden" }}
              >
                <div
                  onClick={() => setOpenSection(openSection === section.key ? null : section.key)}
                  style={{
                    padding: 18,
                    cursor: "pointer",
                    background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <h3 style={{ margin: 0 }}>{section.title}</h3>
                  <span>{openSection === section.key ? "−" : "+"}</span>
                </div>

                {openSection === section.key && (
                  <div style={{ padding: 18, maxHeight: 340, overflowY: "auto" }}>
                    {section.items.map((s) =>
                      renderCard(s, section.badge, section.colors)
                    )}
                  </div>
                )}
              </div>
            )
          )}

          {newItems.length === 0 &&
            inProgressItems.length === 0 &&
            reviewedItems.length === 0 && (
              <p style={{ color: "#64748b" }}>
                No startups match your filters.
              </p>
            )}
        </>
      )}
    </div>
  );
}

export default SharedWithMe;
